import { useState } from "react";
import { StyledAssignCMModal } from "./AssignCMModal.styled";
import { Modal } from "../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import {
  ASSIGN_PC_LABEL,
  CANCEL_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  EDIT_ASSIGN_PC_LABEL,
  EMPTY_PERFORMANCE_COACH_MESSAGE,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  SEARCH_PERFORMANCE_COACH,
} from "../../../../../../constants/Dictionary";
import { ReactComponent as CloseIcon } from "../../../../../../icons/close.svg";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import {
  Collaborator,
  CollaboratorDetails,
} from "../../../../../../types/api/collaborators.type";
import { CollaboratorItem } from "../../../../../../components/CollaboratorItem/CollaboratorItem";
import { useNotification } from "../../../../../../hooks/notification.hook";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import { InfiniteScrollList } from "../../../../../../components/InfiniteScrollList/InfiniteScrollList";
import {
  useModifyCollaboratorCM,
  useCollaboratorsToAssignCM,
} from "../../../../../../hooks/collaboratorProfile.hook";

type AssignCMModalProps = {
  onSave: () => void;
  onClose: () => void;
  collaborator: CollaboratorDetails | null;
  editMode?: boolean;
};

export const AssignCMModal = ({
  onSave,
  onClose,
  collaborator,
  editMode,
}: AssignCMModalProps) => {
  const { notification } = useNotification();
  const showFooter = true;
  const [selectedCM, setSelectedCM] = useState<Collaborator | null>(null);
  const [openPopup, setOpenPopup] = useState(false);
  const collaboratorId = collaborator?.id!;
  const careerManagerId = selectedCM?.id!;

  const { mutateAsync: modifyCollaboratorCM, isPending: isModifying } =
    useModifyCollaboratorCM(collaboratorId);

  const {
    data,
    initLoading,
    canLoadMore,
    fetchMore,
    fetchingMore,
    setSearch,
    search,
    clearSearch,
  } = useCollaboratorsToAssignCM();

  const handleCancel = () => {
    clearSearch();
    setSelectedCM(null);
    onClose();
  };

  const handleSelectCM = (cm: Collaborator) => {
    setSelectedCM(cm);
    setSearch(`${cm.surname} ${cm.name}`);
    setOpenPopup(true);
  };

  const handleConfirmPopup = async () => {
    try {
      await modifyCollaboratorCM(careerManagerId);
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
      clearSearch();
      setOpenPopup(false);
      onSave();
    } catch (error) {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  };

  const handleCancelPopup = () => {
    setOpenPopup(false);
  };

  const getPopupText = () => {
    return selectedCM
      ? `Confermi di voler assegnare il PC ${selectedCM?.surname} ${selectedCM?.name} all'utente ${collaborator?.surname} ${collaborator?.name}?`
      : "";
  };

  return (
    <Modal
      fullSize={false}
      isOpen={true}
      closeModal={handleCancel}
      label={editMode ? EDIT_ASSIGN_PC_LABEL : ASSIGN_PC_LABEL}
      leftButton={
        showFooter ? (
          <Button
            label={CANCEL_LABEL}
            variant={ButtonVariants.SECONDARY}
            icon={<CloseIcon />}
            onClick={handleCancel}
          />
        ) : null
      }
    >
      {!openPopup && (
        <StyledAssignCMModal $showFooter={showFooter}>
          <SearchInput
            value={search}
            onChange={setSearch}
            label={SEARCH_PERFORMANCE_COACH}
            name={"search"}
          />
          <div className="cm-list-container">
            {initLoading || data.length > 0 ? (
              <InfiniteScrollList
                items={data}
                renderItem={(cm) => (
                  <CollaboratorItem
                    key={`collaborator-${cm.id}`}
                    collaboratorInfo={cm}
                    onClick={() => handleSelectCM(cm)}
                  />
                )}
                onScroll={fetchMore}
                loading={initLoading}
                fetchingMore={fetchingMore}
                canLoadMore={canLoadMore}
              />
            ) : (
              search && (
                <BodyTyp variant="3">{EMPTY_PERFORMANCE_COACH_MESSAGE}</BodyTyp>
              )
            )}
          </div>
        </StyledAssignCMModal>
      )}
      {openPopup && (
        <PopupConfirm
          isOpen={openPopup}
          label={editMode ? EDIT_ASSIGN_PC_LABEL : ASSIGN_PC_LABEL}
          popupText={getPopupText()}
          onConfirm={handleConfirmPopup}
          onCancel={handleCancelPopup}
          isLoading={isModifying}
        />
      )}
    </Modal>
  );
};
